import mapboxgl from 'mapbox-gl';

import { bbox, bboxPolygon, booleanIntersects } from '@turf/turf';

import { PtLatLng, Segment } from 'appTypes';
import { Feature, Geometry, LineString } from 'geojson';

import { distanceXy, findNearestSegmentLatLng } from './geography_utils';
import { kSegmentLayerSourceLayerId, kSegmentSourceId } from './map_constants';

export type WithExtras = { extras?: React.ReactNode };

function searchSegmentsForSegmentId(
  segments: Segment[],
  segment_id: string,
): Segment {
  return segments.find(
    (segment) => String(segment.properties.segment_id) === segment_id,
  );
}

export function findNearestSegmentOnMap(
  segments: Segment[],
  position: PtLatLng,
  maxClickDistance: number,
  map,
  excludeId: string = undefined,
): { nearestSegment: Segment; usedExclude: boolean } {
  // eslint-disable-next-line prefer-const
  let { nearestSegment, nearestSegmentPointLatLng } = findNearestSegmentLatLng(
    segments,
    position,
  );
  let usedExclude = false;
  if (nearestSegment && nearestSegment.properties.segment_id === excludeId) {
    usedExclude = true;
    const replacementMatch = findNearestSegmentLatLng(
      segments,
      position,
      excludeId,
    );
    nearestSegment = replacementMatch.nearestSegment;
    nearestSegmentPointLatLng = replacementMatch.nearestSegmentPointLatLng;
  }
  // ! check nearest distance in screen coordinates
  if (nearestSegment !== undefined) {
    const nearestSegmentPtXy = map.project(nearestSegmentPointLatLng);
    const clickPtXy = map.project(position);
    const distXy = distanceXy(clickPtXy, nearestSegmentPtXy);
    if (distXy > maxClickDistance) {
      nearestSegment = undefined;
    }
  }

  return { nearestSegment, usedExclude };
}

export function visibleProperty(visible: boolean) {
  if (visible) {
    return 'visible';
  }
  return 'none';
}

export function setLayerVisibility(
  visible: boolean,
  layerId: string,
  map: mapboxgl.Map,
) {
  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visibleProperty(visible));
  }
}

export function ensureRtlLoaded() {
  // Set up RTL text plugin, with silent failure
  try {
    if (['unavailable', 'error'].includes(mapboxgl.getRTLTextPluginStatus())) {
      mapboxgl.setRTLTextPlugin(
        'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
        (err) => {
          if (err) {
            console.error(err);
          }
        },
      );
    }
  } catch (e) {
    console.error('Failed to load rtl plugin: ', e);
  }
}

export function makeSnakeFromIDs(
  selectedRouteString: String,
  routing: String[],
  map: mapboxgl.Map,
): Feature<LineString> | undefined {
  if (!routing) return undefined;
  const segments = map.querySourceFeatures(kSegmentSourceId, {
    sourceLayer: kSegmentLayerSourceLayerId,
  });
  const coordinates = [];
  const mergable_lines = [];
  const renderedSegs = [];
  for (let i = 0; i < routing.length; i++) {
    if (i > 0 && String(routing[i]) === String(routing[i - 1])) {
      // eslint-disable-next-line no-continue
      continue;
    }
    const selectedSegment = searchSegmentsForSegmentId(
      segments as any,
      String(routing[i]),
    );

    if (selectedSegment) {
      renderedSegs.push(selectedSegment.properties.segment_id);
      const newCords = selectedSegment.geometry.coordinates;
      for (let j = 0; j < newCords.length; j++) {
        if (newCords[j] !== coordinates[coordinates.length - 1]) {
          coordinates.push(newCords[j]);
        }
      }
      mergable_lines.push(selectedSegment);
    }
  }

  const route: Feature<LineString> = {
    type: 'Feature',
    geometry: {
      coordinates,
      type: 'LineString',
    },
    properties: {
      segment_ids: renderedSegs.join(','),
      selected_route: selectedRouteString,
    },
  };
  if (renderedSegs.length > 0) {
    return route;
  } else {
    return undefined;
  }
}

export function addMapLayerInStack(
  map: mapboxgl.Map,
  layerDefinition: mapboxgl.AnyLayer,
  stack: string[],
) {
  const layers = map.getStyle().layers.map((v) => v.id);
  let before;
  const target_layer_index = stack.indexOf(layerDefinition.id);
  if (target_layer_index >= 0 && target_layer_index < stack.length - 1) {
    for (let i = target_layer_index + 1; i < stack.length; i++) {
      if (layers.includes(stack[i])) {
        before = stack[i];
        break;
      }
    }
  }
  // NB if the layer id is not found in the stack, before=undefined and the layer will be added on top as usual.
  // console.debug(`Add layer ${layerDefinition.id} before ${before}`);
  return map.addLayer(layerDefinition, before);
}

export function getGeoJSONLayerData(map, sourceId): undefined | Feature {
  const source = map.getSource(sourceId);
  if (source) {
    return source._data ?? source._options?.data;
  }
  return undefined;
}

export function searchGeoJSONLayerForID(map, sourceId, segmentId) {
  const sourceData = getGeoJSONLayerData(map, sourceId);
  if (sourceData) {
    if (sourceData.properties?.segment_id === segmentId) {
      // eslint-disable-line
      return sourceData; // eslint-disable-line
    }
  }
  return undefined;
}

export function isGeometryOnMap(
  projectBboxPolygon: Geometry,
  map: mapboxgl.Map,
) {
  if (!map) {
    return false;
  }
  const bounds = map.getBounds();
  const mapPolygon = bboxPolygon([
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth(),
  ]);
  return booleanIntersects(mapPolygon, projectBboxPolygon);
}

/**
 * Adjusts the map's zoom level and center to fit the specified bounding box.
 * @param map - The Mapbox GL JS map instance.
 * @param geom - The bounding box to fit.
 */
export const fitBoundsToBbox = (
  map: mapboxgl.Map,
  geom: Geometry,
  paddingProp: number,
) => {
  try {
    const bounds = bbox(geom);
    const height = map.getCanvas().clientHeight;
    map.fitBounds(bounds as mapboxgl.LngLatBoundsLike, {
      padding: height * paddingProp,
    });
  } catch (e) {
    console.error(e);
  }
};
